import React, { useState } from 'react';
import { usePage, Link } from '@inertiajs/react';
import { ArrowLeft, RefreshCw, User, Phone, Briefcase, Calendar, Mail, CheckCircle2, MessageSquare, Clock, AlertCircle, FileText, Send, Image as ImageIcon, Paperclip, Smile, Search, Filter } from 'lucide-react';

interface PageProps {
  invitation: any;
  invitationUser: any;
  messages: any[];
}

export default function WhatsAppThread() {
  const { invitation, invitationUser, messages } = usePage<PageProps>().props;

  return (
    <div className="bg-[#f8f9fa] min-h-screen pb-10">
      {/* Top Navigation */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between sticky top-0 z-50 shadow-sm">
        <div className="flex items-center">
          <Link href={`/invitations/${invitation.id}/manage_users`} className="flex items-center text-gray-600 hover:text-gray-900 font-medium">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </Link>
        </div>
        <h1 className="text-xl font-bold text-gray-800 absolute left-1/2 -translate-x-1/2">WhatsApp Conversation Thread</h1>
        <button className="flex items-center text-gray-600 hover:text-gray-900 border border-gray-300 bg-white rounded-md px-4 py-2 text-sm font-medium shadow-sm transition">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh Thread
        </button>
      </div>

      <div className="max-w-screen-2xl mx-auto px-6 mt-6 flex gap-6">
        
        {/* Left Column: Details & Filters */}
        <div className="w-[320px] shrink-0 space-y-6 flex flex-col h-[calc(100vh-120px)]">
          
          {/* Section 1: Invitee Details */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 flex items-center bg-gray-50/50">
              <div className="bg-[#0f766e] text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold mr-3">1</div>
              <h2 className="text-[15px] font-semibold text-[#0f766e]">Invitee Details</h2>
            </div>
            <div className="p-5 space-y-4 text-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center text-gray-500"><User className="w-4 h-4 mr-2"/> Name</div>
                <div className="font-semibold text-gray-900">{invitationUser.name || 'Unknown'}</div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center text-gray-500"><Phone className="w-4 h-4 mr-2"/> Mobile</div>
                <div className="font-semibold text-gray-900">{invitationUser.phone_number}</div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center text-gray-500"><Briefcase className="w-4 h-4 mr-2"/> Company</div>
                <div className="font-semibold text-gray-900">{invitationUser.organization || '-'}</div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center text-gray-500"><Calendar className="w-4 h-4 mr-2"/> Event</div>
                <div className="font-semibold text-gray-900 truncate max-w-[120px]" title={invitation?.name}>{invitation?.name}</div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center text-gray-500"><FileText className="w-4 h-4 mr-2"/> Invitation Type</div>
                <div className="font-semibold text-gray-900 truncate max-w-[120px]">{invitation?.invitation_type || 'RSVP Invitation'}</div>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                <div className="flex items-center text-gray-500"><CheckCircle2 className="w-4 h-4 mr-2 text-green-500"/> Current Status</div>
                <div className="bg-green-50 text-green-700 px-2.5 py-0.5 rounded-full text-xs font-semibold border border-green-200">
                  {invitationUser.delivery_status || 'Pending'}
                </div>
              </div>
            </div>
          </div>

          {/* Section 2: Conversation Outcome */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 flex items-center bg-gray-50/50">
              <div className="bg-[#0f766e] text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold mr-3">2</div>
              <h2 className="text-[15px] font-semibold text-[#0f766e]">Conversation Outcome</h2>
            </div>
            <div className="p-5 space-y-4 text-sm">
               <div className="flex items-center justify-between">
                <div className="flex items-center text-gray-500"><CheckCircle2 className="w-4 h-4 mr-2"/> Final Status</div>
                <div className="bg-green-50 text-green-700 px-2.5 py-0.5 rounded-full text-xs font-semibold border border-green-200">
                  {invitationUser.response_status ? `RSVP ${invitationUser.response_status}` : 'Pending RSVP'}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center text-gray-500"><Clock className="w-4 h-4 mr-2"/> Last Activity</div>
                <div className="font-semibold text-gray-900">
                  {messages?.length > 0 ? new Date(messages[messages.length - 1].created_at).toLocaleString() : 'N/A'}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center text-gray-500"><MessageSquare className="w-4 h-4 mr-2"/> Unread Replies</div>
                <div className="bg-orange-100 text-orange-700 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold">
                  {messages?.filter(m => m.direction === 'inbound' && m.status !== 'read').length || 0}
                </div>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                <div className="flex items-center text-gray-500"><CheckCircle2 className="w-4 h-4 mr-2"/> RSVP Response</div>
                <div className="font-semibold text-[#0f766e] truncate max-w-[140px]">{invitationUser.response_body || '-'}</div>
              </div>
            </div>
          </div>

          {/* Section 3: Thread Filters */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex-1 flex flex-col">
            <div className="px-5 py-3 border-b border-gray-100 flex items-center bg-gray-50/50">
              <div className="bg-[#0f766e] text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold mr-3">3</div>
              <h2 className="text-[15px] font-semibold text-[#0f766e]">Thread Filters</h2>
            </div>
            <div className="p-5 space-y-5 flex-1 overflow-y-auto text-sm">
               
               <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Message Direction</h3>
                  <div className="flex flex-wrap gap-2">
                     <button className="bg-[#0f766e] text-white px-3 py-1.5 rounded-md text-xs font-medium">All</button>
                     <button className="bg-white border border-gray-300 text-gray-600 px-3 py-1.5 rounded-md text-xs font-medium hover:bg-gray-50">Outbound</button>
                     <button className="bg-white border border-gray-300 text-gray-600 px-3 py-1.5 rounded-md text-xs font-medium hover:bg-gray-50">Inbound</button>
                  </div>
               </div>
               
               <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Message Type</h3>
                  <div className="flex flex-wrap gap-2">
                     <button className="bg-[#0f766e] text-white px-3 py-1.5 rounded-md text-xs font-medium">All</button>
                     <button className="bg-white border border-gray-300 text-gray-600 px-3 py-1.5 rounded-md text-xs font-medium hover:bg-gray-50">Text</button>
                     <button className="bg-white border border-gray-300 text-gray-600 px-3 py-1.5 rounded-md text-xs font-medium hover:bg-gray-50">Template</button>
                     <button className="bg-white border border-gray-300 text-gray-600 px-3 py-1.5 rounded-md text-xs font-medium hover:bg-gray-50">Media</button>
                     <button className="bg-white border border-gray-300 text-gray-600 px-3 py-1.5 rounded-md text-xs font-medium hover:bg-gray-50">RSVP</button>
                  </div>
               </div>

               <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Delivery Status</h3>
                  <div className="flex flex-wrap gap-2">
                     <button className="bg-[#0f766e] text-white px-3 py-1.5 rounded-md text-xs font-medium">All</button>
                     <button className="bg-white border border-gray-300 text-gray-600 px-3 py-1.5 rounded-md text-xs font-medium hover:bg-gray-50">Sent</button>
                     <button className="bg-white border border-gray-300 text-gray-600 px-3 py-1.5 rounded-md text-xs font-medium hover:bg-gray-50">Delivered</button>
                     <button className="bg-white border border-gray-300 text-gray-600 px-3 py-1.5 rounded-md text-xs font-medium hover:bg-gray-50">Read</button>
                     <button className="bg-white border border-gray-300 text-gray-600 px-3 py-1.5 rounded-md text-xs font-medium hover:bg-gray-50">Failed</button>
                     <button className="bg-white border border-gray-300 text-gray-600 px-3 py-1.5 rounded-md text-xs font-medium hover:bg-gray-50">Replied</button>
                  </div>
               </div>

            </div>
          </div>

          <div className="flex flex-col gap-3">
             <button className="w-full bg-[#0f766e] hover:bg-[#0d645e] text-white font-semibold py-2.5 px-4 rounded-lg shadow-sm flex items-center justify-center text-sm transition">
                <FileText className="w-4 h-4 mr-2" />
                View Full Conversation Log
             </button>
             <button className="w-full bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-semibold py-2.5 px-4 rounded-lg shadow-sm flex items-center justify-center text-sm transition">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Invitations
             </button>
          </div>
        </div>

        {/* Right Column: Chat Interface & Metadata */}
        <div className="flex-1 flex flex-col gap-6">
           
           {/* Chat UI */}
           <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col flex-1">
              
              {/* Chat Header */}
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center">
                   <div className="bg-[#0f766e] text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold mr-3">3</div>
                   <h2 className="text-lg font-semibold text-gray-800">Conversation History</h2>
                </div>
                <div className="flex items-center space-x-3">
                   <div className="relative">
                      <input type="text" placeholder="Search messages..." className="pl-8 pr-3 py-1.5 border border-gray-300 rounded-md text-sm w-48 focus:border-[#0f766e] focus:ring-[#0f766e] shadow-sm"/>
                      <Search className="absolute left-2.5 top-2 w-4 h-4 text-gray-400" />
                   </div>
                   <div className="relative">
                      <select className="pl-8 pr-8 py-1.5 border border-gray-300 rounded-md text-sm text-gray-600 focus:border-[#0f766e] focus:ring-[#0f766e] shadow-sm appearance-none bg-white">
                         <option>Today</option>
                         <option>Yesterday</option>
                         <option>Last 7 Days</option>
                      </select>
                      <Calendar className="absolute left-2.5 top-2 w-4 h-4 text-gray-400" />
                      <div className="absolute right-2.5 top-2.5 pointer-events-none">
                         <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                      </div>
                   </div>
                   <div className="flex bg-gray-100 p-0.5 rounded-md border border-gray-200">
                      <button className="px-3 py-1 bg-[#0f766e] text-white rounded text-xs font-semibold shadow-sm">All Messages</button>
                      <button className="px-3 py-1 text-gray-600 hover:text-gray-900 rounded text-xs font-semibold transition">Sent</button>
                      <button className="px-3 py-1 text-gray-600 hover:text-gray-900 rounded text-xs font-semibold transition">Received</button>
                   </div>
                </div>
              </div>

              {/* Chat Messages Area */}
              <div className="flex-1 bg-[#f0f2f5] p-6 overflow-y-auto space-y-6" style={{ backgroundImage: "url('https://i.pinimg.com/originals/8f/ba/cb/8fbacbd464e996966eb9d4a6b7a9c21e.jpg')", backgroundSize: 'cover', backgroundBlendMode: 'overlay', backgroundColor: 'rgba(240, 242, 245, 0.9)' }}>
                 
                 <div className="flex justify-center mb-6">
                    <span className="bg-white text-gray-500 text-xs font-medium px-3 py-1 rounded-lg shadow-sm border border-gray-100">Today</span>
                 </div>

                 {messages?.map((msg) => (
                    <div key={msg.id} className={`flex ${msg.direction === 'outbound' ? 'justify-end' : 'justify-start'} mb-4 relative`}>
                       
                       {/* Time label on the side */}
                       {msg.direction === 'outbound' && (
                          <div className="mr-3 mt-1 text-[11px] text-gray-500 font-medium">{new Date(msg.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</div>
                       )}

                       <div className="flex items-start max-w-[65%]">
                          {msg.direction === 'inbound' && (
                             <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-gray-600 font-bold text-sm mr-2 shadow-sm shrink-0">
                               {invitationUser.name ? invitationUser.name.charAt(0).toUpperCase() : 'U'}
                             </div>
                          )}
                          
                          <div className="flex flex-col">
                             {/* Message Metadata Header (Sender Type / Status) */}
                             <div className={`flex items-center text-[10px] text-gray-500 mb-1 font-medium ${msg.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}>
                               {msg.direction === 'outbound' ? (
                                  <>
                                     <MessageSquare className="w-3 h-3 mr-1" />
                                     Outbound • {msg.template_sid ? 'Template' : 'Text'}
                                  </>
                               ) : (
                                  <>
                                     <User className="w-3 h-3 mr-1" />
                                     Inbound • Text
                                  </>
                               )}
                             </div>

                             {/* Message Bubble */}
                             <div className={`rounded-xl shadow-sm overflow-hidden ${msg.direction === 'outbound' ? 'bg-[#d9fdd3] border border-[#c3e8be]' : 'bg-white border border-gray-100'} ${msg.direction === 'outbound' ? 'rounded-tr-none' : 'rounded-tl-none'}`}>
                                
                                <div className="p-3 text-[14px] text-gray-800 leading-snug whitespace-pre-wrap">
                                   {msg.body || 'Media Message'}
                                </div>
                                
                             </div>

                             {/* Read Receipts */}
                             {msg.direction === 'outbound' && (
                                <div className="flex justify-end items-center mt-1 space-x-1">
                                   <span className={`text-[10px] font-semibold ${['read', 'delivered', 'sent'].includes(msg.status) ? 'text-green-600' : 'text-gray-500'}`}>
                                      {msg.status ? msg.status.charAt(0).toUpperCase() + msg.status.slice(1) : 'Sent'} {msg.status === 'read' ? '✓✓' : (msg.status === 'delivered' ? '✓✓' : '✓')}
                                   </span>
                                </div>
                             )}
                          </div>
                       </div>

                       {msg.direction === 'inbound' && (
                          <div className="ml-3 mt-1 text-[11px] text-gray-500 font-medium">{new Date(msg.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</div>
                       )}

                    </div>
                 ))}

              </div>

              {/* Send Manual Message Input */}
              <div className="bg-white border-t border-gray-200 p-4">
                 <div className="flex items-center mb-2">
                    <div className="bg-[#0f766e] text-white w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold mr-2">4</div>
                    <h3 className="text-sm font-semibold text-gray-700">Send Manual Message</h3>
                 </div>
                 
                 <div className="relative border border-gray-300 rounded-lg shadow-sm bg-gray-50 focus-within:ring-1 focus-within:ring-[#0f766e] focus-within:border-[#0f766e] transition-all">
                    <textarea 
                       className="w-full bg-transparent border-0 focus:ring-0 resize-none p-3 text-sm text-gray-800 placeholder-gray-400"
                       rows={2}
                       placeholder="Type a WhatsApp message..."
                    ></textarea>
                    
                    <div className="flex items-center justify-between px-3 py-2 border-t border-gray-200 bg-white rounded-b-lg">
                       <div className="flex space-x-1">
                          <button className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition"><Paperclip className="w-4 h-4" /></button>
                          <button className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition"><ImageIcon className="w-4 h-4" /></button>
                          <button className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition"><Smile className="w-4 h-4" /></button>
                          <div className="w-px h-5 bg-gray-300 mx-1 my-auto"></div>
                          <button className="flex items-center px-2 py-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition text-xs font-medium">
                             <FileText className="w-3.5 h-3.5 mr-1" /> Use Template
                          </button>
                       </div>
                       
                       <button className="bg-[#0f766e] hover:bg-[#0d645e] text-white font-medium py-1.5 px-4 rounded shadow-sm flex items-center text-sm transition">
                          <Send className="w-3.5 h-3.5 mr-1.5" /> Send Message
                       </button>
                    </div>
                 </div>
                 <div className="flex items-center mt-2 text-xs text-blue-600 bg-blue-50/50 p-2 rounded">
                    <AlertCircle className="w-3.5 h-3.5 mr-1.5" />
                    <span>Manual messages are sent through the approved WhatsApp channel. You must be within the 24-hour service window for free-form text.</span>
                 </div>
              </div>
           </div>

           {/* Message Metadata Table */}
           <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
             <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/50">
               <h3 className="text-sm font-semibold text-[#0f766e]">Message Metadata</h3>
             </div>
             <div className="overflow-x-auto">
               <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                     <tr>
                        <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Time</th>
                        <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Direction</th>
                        <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Type</th>
                        <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                        <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Provider Message ID</th>
                     </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200 text-sm">
                     {messages?.map((msg) => (
                       <tr key={msg.id} className="hover:bg-gray-50 transition">
                          <td className="px-5 py-3 whitespace-nowrap text-gray-600">{new Date(msg.created_at).toLocaleString()}</td>
                          <td className="px-5 py-3 whitespace-nowrap">
                            <span className={`flex items-center ${msg.direction === 'outbound' ? 'text-green-600' : 'text-blue-600'} font-medium`}>
                              {msg.direction === 'outbound' ? (
                                <><svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 10l7-7m0 0l7 7m-7-7v18"></path></svg> Outbound</>
                              ) : (
                                <><svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"></path></svg> Inbound</>
                              )}
                            </span>
                          </td>
                          <td className="px-5 py-3 whitespace-nowrap text-gray-600 flex items-center">
                            <MessageSquare className="w-4 h-4 mr-1 text-gray-400"/> {msg.template_sid ? 'Template' : 'Text'}
                          </td>
                          <td className="px-5 py-3 whitespace-nowrap">
                             <span className="px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-50 text-gray-700 border border-gray-200">
                               {msg.status ? msg.status.charAt(0).toUpperCase() + msg.status.slice(1) : 'Sent'}
                             </span>
                          </td>
                          <td className="px-5 py-3 whitespace-nowrap text-gray-500 font-mono text-xs">
                             {msg.message_sid || '-'}
                          </td>
                       </tr>
                     ))}
                     {(!messages || messages.length === 0) && (
                       <tr>
                         <td colSpan={5} className="px-5 py-8 text-center text-gray-500">No messages found.</td>
                       </tr>
                     )}
                  </tbody>
               </table>
             </div>
           </div>

        </div>

      </div>
    </div>
  );
}
